import React, { useEffect, useState, useCallback } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";
import { useAuth } from "../../App";

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" }) : "—";
const fmtTime = d => d ? new Date(d).toLocaleString("en-IN", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" }) : "—";
const fmtAmt  = n => parseFloat(n||0).toLocaleString("en-IN", { minimumFractionDigits:2 });

const CAT_ICON = {
  Travel:"🚂", Hotel:"🏨", Food:"🍽️", Fuel:"⛽",
  Courier:"📦", Stationeries:"📝", Prints:"🖨️", Miscellaneous:"📌",
};

const ROLE_BADGE = {
  manager:    { bg:"#DBEAFE", color:"#1D4ED8", label:"Manager"    },
  admin:      { bg:"#FEF3C7", color:"#D97706", label:"Admin"      },
  superadmin: { bg:"#EDE9FE", color:"#7C3AED", label:"Super Admin"},
  employee:   { bg:"#D1FAE5", color:"#065F46", label:"Employee"   },
};

// ─── Ask Query Modal (for manager/admin/superadmin) ───────────────────────────
function AskQueryModal({ expense, onClose, onSent }) {
  const [question, setQuestion] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setSaving(true);
    try {
      await API.post(`/queries/expense/${expense.expense_id}`, { question });
      toast.success("Query sent to employee");
      onSent();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to send query");
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2 style={{ fontSize: 17, fontWeight: 700 }}>❓ Ask a Query</h2>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
        </div>
        <form onSubmit={handleSend}>
          <div className="modal-body">
            {/* Expense context */}
            <div style={{ background:"#f9fafb", borderRadius:8, padding:"12px 14px", marginBottom:16, fontSize:13 }}>
              <div style={{ fontWeight:700, color:"#1f2937", marginBottom:4 }}>
                {CAT_ICON[expense.category] || "📌"} {expense.category}
                <span style={{ color:"#059669", fontWeight:800, marginLeft:8 }}>₹{fmtAmt(expense.amount)}</span>
              </div>
              <div style={{ color:"#6b7280" }}>{expense.description}</div>
              <div style={{ color:"#4b5563", fontSize:11, marginTop:4 }}>
                {fmtDate(expense.expense_date)} · {expense.trip_title}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Your Question *</label>
              <textarea
                className="form-control"
                rows={4}
                placeholder="e.g. Please provide the original bill / Can you clarify the purpose of this expense?"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                required
                autoFocus
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Sending..." : "📨 Send Query"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Reply Modal (for employee) ───────────────────────────────────────────────
function ReplyModal({ query, onClose, onReplied }) {
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSaving(true);
    try {
      await API.post(`/queries/${query.id}/reply`, { reply });
      toast.success("Reply sent!");
      onReplied();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to send reply");
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h2 style={{ fontSize: 17, fontWeight: 700 }}>💬 Reply to Query</h2>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
        </div>
        <form onSubmit={handleSend}>
          <div className="modal-body">
            {/* Original question */}
            <div style={{ background:"#EFF6FF", borderLeft:"3px solid #3B82F6", borderRadius:"0 8px 8px 0", padding:"12px 14px", marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:700, color:"#1D4ED8", textTransform:"uppercase", marginBottom:6 }}>
                ❓ Query from {query.asker_name}
              </div>
              <div style={{ fontSize:13, color:"#1e3a5f", lineHeight:1.6 }}>{query.question}</div>
              <div style={{ fontSize:11, color:"#4b5563", marginTop:6 }}>
                {CAT_ICON[query.category] || "📌"} {query.category} · ₹{fmtAmt(query.amount)} · {fmtDate(query.expense_date)}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Your Reply *</label>
              <textarea
                className="form-control"
                rows={4}
                placeholder="Provide the requested details or clarification..."
                value={reply}
                onChange={e => setReply(e.target.value)}
                required
                autoFocus
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Sending..." : "✅ Send Reply"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Single Query Card ────────────────────────────────────────────────────────
function QueryCard({ q, isEmployee, onReply, onRemind, onMarkSeen }) {
  const [reminding, setReminding] = useState(false);

  const handleRemind = async () => {
    setReminding(true);
    try {
      await onRemind(q.id);
    } finally { setReminding(false); }
  };

  const isOpen      = q.status === "open";
  const hasNewReply = q.status === "closed" && !q.reply_seen && !isEmployee;
  const hasExpenseContext = Boolean(q.category || q.expense_date || q.description);

  return (
    <div style={{
      background: "white",
      border: `1px solid ${hasNewReply ? "#3B82F6" : isOpen && isEmployee ? "#F59E0B" : "#e5e7eb"}`,
      borderLeft: `4px solid ${hasNewReply ? "#3B82F6" : isOpen && isEmployee ? "#F59E0B" : isOpen ? "#9ca3af" : "#10B981"}`,
      borderRadius: 10,
      overflow: "hidden",
      boxShadow: hasNewReply ? "0 0 0 2px #DBEAFE" : "none",
    }}>
      {/* Header */}
      <div style={{ padding:"14px 18px", background:"#fafafa", borderBottom:"1px solid #f3f4f6", display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:8 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <span style={{ fontSize:20 }}>{CAT_ICON[q.category] || "📌"}</span>
          <div>
            <div style={{ fontWeight:700, fontSize:14, color:"#1f2937" }}>
              {q.category || "Previous expense query"}
              {q.amount != null && <span style={{ color:"#059669", fontWeight:800, marginLeft:8 }}>₹{fmtAmt(q.amount)}</span>}
            </div>
            {!hasExpenseContext && (
              <div style={{ fontSize:11, color:"#92400e", marginTop:3 }}>
                The original expense was removed or replaced, but this query still requires your reply.
              </div>
            )}
            <div style={{ fontSize:12, color:"#6b7280" }}>
              {fmtDate(q.expense_date)}
              {q.trip_title && <span> · ✈️ {q.trip_title}</span>}
              {q.destination && <span> · 📍 {q.destination}</span>}
            </div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {/* Status pill */}
          <span style={{
            fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20,
            background: isOpen ? "#FEF3C7" : "#D1FAE5",
            color:      isOpen ? "#92400E" : "#065F46",
            border: `1px solid ${isOpen ? "#F59E0B44" : "#10B98144"}`,
          }}>
            {isOpen ? "⏳ Awaiting Reply" : "✅ Answered"}
          </span>
          {/* New reply badge */}
          {hasNewReply && (
            <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, background:"#DBEAFE", color:"#1D4ED8", border:"1px solid #93C5FD" }}>
              🔔 New Reply
            </span>
          )}
          {/* Reminder count */}
          {!isEmployee && q.reminder_count > 0 && (
            <span style={{ fontSize:11, color:"#4b5563" }}>
              ⏰ {q.reminder_count} reminder{q.reminder_count > 1 ? "s" : ""} sent
            </span>
          )}
        </div>
      </div>

      {/* Question */}
      <div style={{ padding:"14px 18px" }}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:".05em", marginBottom:6 }}>
            {isEmployee ? `❓ From ${q.asker_name}` : `❓ You asked ${q.employee_name || "employee"}`}
            <span style={{ color:"#4b5563", fontWeight:400, marginLeft:8, textTransform:"none", fontSize:10 }}>
              {fmtTime(q.asked_at)}
            </span>
          </div>
          <div style={{ fontSize:14, color:"#1f2937", lineHeight:1.7, background:"#f9fafb", borderRadius:8, padding:"10px 12px" }}>
            {q.question}
          </div>
        </div>

        {/* Reply — if answered */}
        {q.reply && (
          <div style={{ marginBottom:12, paddingLeft:16, borderLeft:"3px solid #10B981" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#065F46", textTransform:"uppercase", letterSpacing:".05em", marginBottom:6 }}>
              💬 {isEmployee ? "Your Reply" : `${q.employee_name || "Employee"}'s Reply`}
              <span style={{ color:"#4b5563", fontWeight:400, marginLeft:8, textTransform:"none", fontSize:10 }}>
                {fmtTime(q.replied_at)}
              </span>
            </div>
            <div style={{ fontSize:14, color:"#374151", lineHeight:1.7 }}>
              {q.reply}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display:"flex", gap:8, marginTop:8, justifyContent:"flex-end" }}>
          {/* Employee: reply button (open queries only) */}
          {isEmployee && isOpen && (
            <button className="btn btn-primary btn-sm" onClick={() => onReply(q)}>
              💬 Reply
            </button>
          )}
          {/* Manager/Admin: remind button (open queries only) */}
          {!isEmployee && isOpen && (
            <button
              className="btn btn-sm"
              style={{ background:"#FEF3C7", color:"#92400E", border:"1px solid #F59E0B44", fontWeight:700 }}
              disabled={reminding}
              onClick={handleRemind}
            >
              {reminding ? "Sending..." : "⏰ Send Reminder"}
            </button>
          )}
          {/* Manager/Admin: mark seen button (new replies) */}
          {!isEmployee && hasNewReply && (
            <button
              className="btn btn-sm"
              style={{ background:"#DBEAFE", color:"#1D4ED8", border:"1px solid #93C5FD", fontWeight:700 }}
              onClick={() => onMarkSeen(q.id)}
            >
              ✓ Mark as Read
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function QueryInbox() {
  const { user } = useAuth();
  // Navigation, badges and the API all use the Expense-specific role override.
  // Using the employee's base HRMS role here made an Expense employee with an
  // HRMS manager title load /queries/sent instead of their /queries/inbox.
  const expenseRole = user?.app_role_overrides?.expense
    || user?.app_role_overrides?.expenses
    || user?.role
    || "employee";
  const isEmployee   = expenseRole === "employee";
  const isAdminLevel = expenseRole === "admin" || expenseRole === "superadmin";

  const [queries,   setQueries]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState("all");       // all | open | answered
  const [showAll,   setShowAll]   = useState(false);       // admin: show team queries too
  const [replyQ,    setReplyQ]    = useState(null);        // query being replied to
  const [askExp,    setAskExp]    = useState(null);        // expense being queried (for demo)

  const load = useCallback(() => {
    setLoading(true);
    const url = isEmployee
      ? "/queries/inbox"
      : `/queries/sent${showAll ? "?all=true" : ""}`;

    API.get(url)
      .then(r => { setQueries(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [isEmployee, showAll]);

  useEffect(() => { load(); }, [load]);

  // When a reply is sent, refresh and mark seen
  const handleReplied = () => load();

  const handleRemind = async (queryId) => {
    try {
      const r = await API.post(`/queries/${queryId}/remind`);
      toast.success(r.data.message || "Reminder sent!");
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to send reminder");
    }
  };

  const handleMarkSeen = async (queryId) => {
    try {
      await API.patch(`/queries/${queryId}/seen`);
      setQueries(prev => prev.map(q => q.id === queryId ? { ...q, reply_seen: true } : q));
    } catch { /* silent */ }
  };

  const handleMarkAllSeen = async () => {
    const unseen = queries.filter(q => q.status === "closed" && !q.reply_seen);
    await Promise.all(unseen.map(q => API.patch(`/queries/${q.id}/seen`).catch(() => {})));
    setQueries(prev => prev.map(q => ({ ...q, reply_seen: true })));
    toast.success("All replies marked as read");
  };

  // Filtered list
  const displayed = queries.filter(q => {
    if (filter === "open")     return q.status === "open";
    if (filter === "answered") return q.status === "closed";
    return true;
  });

  // Counts for summary bar
  const openCount      = queries.filter(q => q.status === "open").length;
  const answeredCount  = queries.filter(q => q.status === "closed").length;
  const newReplies     = queries.filter(q => q.status === "closed" && !q.reply_seen && !isEmployee).length;

  const rb = ROLE_BADGE[expenseRole] || ROLE_BADGE.employee;

  return (
    <div>
      {/* Page title */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
            <h1 className="page-title" style={{ margin:0 }}>
              🔔 Queries & Responses
            </h1>
            {(isEmployee ? openCount : newReplies) > 0 && (
              <span style={{
                background:"#DC2626", color:"white", borderRadius:20,
                padding:"2px 10px", fontSize:13, fontWeight:800, minWidth:24, textAlign:"center"
              }}>
                {isEmployee ? openCount : newReplies}
              </span>
            )}
          </div>
          <p className="page-subtitle" style={{ margin:0 }}>
            {isEmployee
              ? "Questions from your manager/admin about your expenses — reply to keep your claims moving"
              : "Queries you've raised with employees — track replies here"}
          </p>
        </div>

        {/* Admin/superadmin: toggle all-team queries */}
        {isAdminLevel && (
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button
              onClick={() => setShowAll(false)}
              style={{ padding:"7px 14px", borderRadius:8, border:"1px solid", fontWeight:600, fontSize:12, cursor:"pointer",
                background: !showAll ? "#1d4ed8" : "white", color: !showAll ? "white" : "#374151", borderColor: !showAll ? "#1d4ed8" : "#d1d5db" }}>
              My Queries
            </button>
            <button
              onClick={() => setShowAll(true)}
              style={{ padding:"7px 14px", borderRadius:8, border:"1px solid", fontWeight:600, fontSize:12, cursor:"pointer",
                background: showAll ? "#1d4ed8" : "white", color: showAll ? "white" : "#374151", borderColor: showAll ? "#1d4ed8" : "#d1d5db" }}>
              All Team Queries
            </button>
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px,1fr))", gap:14, marginBottom:24 }}>
        {isEmployee ? (
          <>
            <SummaryCard icon="⏳" label="Awaiting Reply"  value={openCount}     color="#92400e" bg="#FEF3C7" />
            <SummaryCard icon="✅" label="Answered"        value={answeredCount}  color="#065F46" bg="#D1FAE5" />
            <SummaryCard icon="📋" label="Total Queries"  value={queries.length} color="#1D4ED8" bg="#DBEAFE" />
          </>
        ) : (
          <>
            <SummaryCard icon="🔔" label="New Replies"     value={newReplies}     color="#1D4ED8" bg="#DBEAFE" />
            <SummaryCard icon="⏳" label="Awaiting Reply"  value={openCount}      color="#92400e" bg="#FEF3C7" />
            <SummaryCard icon="✅" label="Resolved"        value={answeredCount}  color="#065F46" bg="#D1FAE5" />
            <SummaryCard icon="📋" label="Total Sent"     value={queries.length} color="#374151" bg="#f3f4f6" />
          </>
        )}
      </div>

      {/* Filter + bulk actions */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, flexWrap:"wrap", gap:8 }}>
        <div style={{ display:"flex", gap:6 }}>
          {[["all","All"],["open","Open"],["answered","Answered"]].map(([k,l]) => (
            <button key={k} onClick={() => setFilter(k)}
              style={{ padding:"6px 14px", borderRadius:20, fontSize:13, fontWeight:600, cursor:"pointer", border:"1px solid",
                background: filter===k ? "#1f2937" : "white",
                color:      filter===k ? "white"   : "#374151",
                borderColor:filter===k ? "#1f2937" : "#d1d5db" }}>
              {l}
              <span style={{ marginLeft:5, fontSize:11, opacity:.7 }}>
                ({k==="all" ? queries.length : k==="open" ? openCount : answeredCount})
              </span>
            </button>
          ))}
        </div>
        {/* Mark all as read */}
        {!isEmployee && newReplies > 0 && (
          <button
            onClick={handleMarkAllSeen}
            style={{ padding:"6px 14px", borderRadius:8, border:"1px solid #93C5FD", background:"#DBEAFE", color:"#1D4ED8", fontWeight:700, fontSize:12, cursor:"pointer" }}>
            ✓ Mark All as Read ({newReplies})
          </button>
        )}
      </div>

      {/* Query list */}
      {loading ? (
        <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>Loading queries...</div>
      ) : displayed.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div style={{ fontSize:52 }}>
              {isEmployee ? "🎉" : "❓"}
            </div>
            <h3>
              {filter === "all"
                ? isEmployee ? "No queries from your manager yet" : "No queries sent yet"
                : filter === "open" ? "No open queries" : "No answered queries"}
            </h3>
            <p style={{ color:"#9ca3af" }}>
              {isEmployee
                ? "When your manager or admin has a question about an expense, it will appear here."
                : "Use the query button on any expense in Approvals or Expense Report to ask a question."}
            </p>
          </div>
        </div>
      ) : (
        <div style={{ display:"grid", gap:14 }}>
          {displayed.map(q => (
            <QueryCard
              key={q.id}
              q={q}
              isEmployee={isEmployee}
              onReply={setReplyQ}
              onRemind={handleRemind}
              onMarkSeen={handleMarkSeen}
            />
          ))}
        </div>
      )}

      {/* Reply modal */}
      {replyQ && (
        <ReplyModal
          query={replyQ}
          onClose={() => setReplyQ(null)}
          onReplied={handleReplied}
        />
      )}

      {/* Ask query modal (for manual use) */}
      {askExp && (
        <AskQueryModal
          expense={askExp}
          onClose={() => setAskExp(null)}
          onSent={load}
        />
      )}
    </div>
  );
}

// ─── Summary card sub-component ───────────────────────────────────────────────
function SummaryCard({ icon, label, value, color, bg }) {
  return (
    <div className="card" style={{ padding:"16px 18px", display:"flex", gap:14, alignItems:"center" }}>
      <div style={{ fontSize:26, background:bg, width:48, height:48, borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center" }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize:10, color:"#6b7280", fontWeight:600, textTransform:"uppercase", marginBottom:2 }}>{label}</div>
        <div style={{ fontSize:22, fontWeight:800, color }}>{value}</div>
      </div>
    </div>
  );
}
